<template lang="pug">
div
    div(v-if='value || milSymbol' ref='svg' v-html='svg' :style='svgStyle')
    //- if country information is available, display the flag
    .countryFlagStyle(v-if='milSymbol?.country')
        CountryFlag(:country='milSymbol?.country' size='small')
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import type { PropType } from 'vue';
import ms from 'milsymbol';
import MilSymbolDTO from '@/models/MilSymbol';
import CountryFlag from 'vue-country-flag-next';

export default defineComponent({
    components: {
        CountryFlag,
    },
    name: 'MilSymbol',
    props: {
        value: {
            // If just the icon code is passed here, it will be used to generate the symbol
            type: String,
            default: undefined,
        },
        uniqueDesignation: {
            type: String,
            default: undefined,
        },
        milSymbol: {
            type: Object as PropType<MilSymbolDTO>,
            default: undefined,
        },
        size: {
            type: Number,
            default: 40,
        },
        outlineColor: {
            type: String,
            default: `#121212`,
        },
        defaultStyle: {
            // If true, the default style will be used. If false, styling suitable for a dark background will be used.
            type: Boolean,
            default: false,
        },
        compactLayout: {
            type: Boolean,
            default: false,
        },
        ellipsesMaxChars: {
            type: Number,
            default: 9,
        },
    },
    computed: {
        svgStyle() {
            const anchor = (this as any).symbol.getAnchor();
            if (this.compactLayout) {
                const nodeSizeAdjustment = 70;
                return {
                    margin: `0 ${anchor.x - nodeSizeAdjustment}px  0 0`,
                };
            }
            return {
                // 33.83 is the default anchor position with no text
                margin: `0 ${anchor.x}px 0 0`,
            };
        },
        svg(): string {
            const symbol = (this as any).symbol;
            const svg = symbol.asSVG();
            return svg;
        },
        resolvedIconCode(): string | undefined {
            let resolvedIconCode: string = undefined;

            // Some reverse-compatibility to accept the icon code or the MilSymbol type
            if (this.milSymbol) {
                resolvedIconCode = (this.milSymbol as MilSymbolDTO)?.code;
            } else if (this.value) {
                if (typeof this.value === 'string') {
                    resolvedIconCode = this.value;
                } else {
                    resolvedIconCode = (this.milSymbol as MilSymbolDTO)?.code;
                }
            }

            return resolvedIconCode;
        },
        resolvedUniqueDesignation() {
            let uniqueDesignation = '';
            if ((this as any).milSymbol?.uniqueDesignation)
                uniqueDesignation = (this as any).milSymbol.uniqueDesignation;
            if ((this as any).uniqueDesignation) {
                uniqueDesignation = (this as any).uniqueDesignation;
            }
            return uniqueDesignation;
        },

        options() {
            const myColors = ms.ColorMode(
                'rgb(128,0,128)',
                'rgb(0,168,220)',
                'rgb(255,48,49)',
                'rgb(0,226,110)',
                'rgb(255,255,0)',
            );
            // infoColor controls both the colour of
            // 1) the unique designation (on a black background next to the icon) and
            // 2) the special headquarters (on the white background that is the inside of the icon)
            // So it needs to be visible on both.
            const infoColor = ms.ColorMode(
                'rgb(128,0,128)',
                // 'rgb(255,255,255)',  // This will make the special headquarters text invisible.
                'rgb(0,168,220)',
                'rgb(255,48,49)',
                'rgb(0,226,110)',
                'rgb(255,255,0)',
            );

            const white = ms.ColorMode('white', 'white', 'white', 'white', 'white');

            const defaultOptions: ms.SymbolOptions = {
                outlineWidth: 6,
                size: (this.size as number) / 1.8,
                standard: '2525',
                strokeWidth: this.size >= 50 ? 6 : 10,
            };
            const colorsForDarkBackground = {
                outlineColor: this.outlineColor as string,
                frameColor: myColors,
                iconColor: myColors,
                colorMode: white,
                infoColor: infoColor,
            };

            const options: ms.SymbolOptions = {
                ...defaultOptions,
                ...(this.defaultStyle ? {} : colorsForDarkBackground),
            };

            if ((this as any).milSymbol?.specialHeadquarters)
                options.specialHeadquarters = (this as any).milSymbol.specialHeadquarters;

            if ((this as any).milSymbol?.reinforcedReduced)
                options.reinforcedReduced = (this as any).milSymbol.reinforcedReduced;
            const truncatedUniqueDesignationText = this.truncateWithEllipsis(
                this.resolvedUniqueDesignation,
                this.ellipsesMaxChars,
            );

            options.hqStaffLength = '40';
            options.infoSize = '30';
            options.uniqueDesignation = truncatedUniqueDesignationText;
            if (this.compactLayout) {
                options.uniqueDesignation = '';
                options.additionalInformation = truncatedUniqueDesignationText;
            }

            return options;
        },
        symbol() {
            if (this.options.uniqueDesignation === undefined) {
                const msg =
                    'options.uniqueDesignation is undefined, which will cause an error when passed to ms.Symbol';
                alert(msg);
                throw new Error(msg);
            }
            const symbol = new ms.Symbol(this.resolvedIconCode, this.options);
            return symbol;
        },
    },
    methods: {
        truncateWithEllipsis(text, maxLength) {
            if (typeof text !== 'string' || !Number.isInteger(maxLength) || maxLength < 0) {
                throw new Error('Invalid arguments');
            }

            return text.length > maxLength ? text.slice(0, maxLength - 3) + '...' : text;
        },
    },
});
</script>

<style scoped>
.my-sheet {
    margin: 0 30px 0 0;
}
.debug {
    font-size: 10px;
}
.countryFlagStyle {
    position: absolute;
    bottom: 0;
    right: -10px;
    z-index: 1;
}
</style>
